import { useEffect, useState } from 'react';
import { Icon } from '../../icons';
import { Alert, Btn, Card, SectionHeader, FormRow, Field, Tabs, Modal } from '../../components/ui';
import { getSettings, updateSettings } from '../../api/settings';
import {
  cancelAccountDeletion,
  downloadPrivacyExport,
  getPrivacyDashboard,
  getPrivacyExports,
  requestAccountDeletion,
  requestAuditLogPrivacyExport,
  requestFinancialPrivacyExport,
  requestFullPrivacyExport,
  deactivateAccount,
  deleteAccountImmediately,
  getPrivacyConfig,
} from '../../api/privacy';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';
import { toastSuccess, toastError } from '../../utils/toast';

const translations = { en, hi, gu };

const DEFAULT_NOTIFICATIONS = {
  email_maintenance: true,
  email_payments: true,
  email_vendors: true,
  email_alerts: true,
  sms_payments: true,
  sms_alerts: true,
  sms_maintenance: false,
  push_all: true,
  push_fire: true,
  push_tasks: true,
  digest_daily: false,
  digest_weekly: true,
  push_promo: false,
  sms_promo: false,
  email_promo: false,
};

const DELETE_REASON_OPTIONS = [
  'No longer needed',
  'Switching platform',
  'Privacy concerns',
  'Technical issues',
  'Other',
];

const timeoutOptions = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
  { value: 1440, label: '24 hours' },
];

function Toggle({ on, onToggle, label, desc }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-700 last:border-0">
      <div className="pr-4">
        <div className="text-sm font-semibold text-ink dark:text-slate-100">{label}</div>
        {desc && <div className="text-xs text-muted dark:text-slate-400 mt-0.5">{desc}</div>}
      </div>
      <button
        type="button"
        onClick={onToggle}
        className={`flex-shrink-0 border-0 p-0 cursor-pointer relative transition-colors ${on ? 'bg-[#4f46e5]' : 'bg-slate-300 dark:bg-slate-600'}`}
        style={{ width: 40, height: 22, borderRadius: 11 }}
      >
        <span
          className="bg-white shadow-sm"
          style={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            position: 'absolute',
            top: 3,
            left: on ? 21 : 3,
            transition: 'left .2s ease',
          }}
        />
      </button>
    </div>
  );
}

export default function SettingsScreen() {
  const { language: ctxLang = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[ctxLang]?.settings?.[key] || translations.en?.settings?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };

  const DELETE_REASON_OPTIONS = [
    t('delete_reason_1'),
    t('delete_reason_2'),
    t('delete_reason_3'),
    t('delete_reason_4'),
    t('delete_reason_5'),
  ];

  const timeoutOptions = [
    { value: 15, label: t('timeout_15') },
    { value: 30, label: t('timeout_30') },
    { value: 60, label: t('timeout_60') },
    { value: 240, label: t('timeout_240') },
    { value: 480, label: t('timeout_480') },
    { value: 1440, label: t('timeout_1440') },
  ];
  const {
    loadPreferences,
    savePreferences,
    applyTheme,
    dateFormat,
    theme,
    language,
    security,
    securityLoading,
    securitySaving,
    securitySetup,
    saveSecuritySettings,
    verifyTwoFactorSetup,
    disableTwoFactor,
    regenerateRecoveryCodes,
    loadSecuritySettings,
  } = useSettings();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notif, setNotif] = useState(DEFAULT_NOTIFICATIONS);
  const [appearance, setAppearance] = useState({
    date_format: dateFormat || 'DD/MM/YYYY',
    theme: theme || 'system',
    language: language || 'en',
  });
  const [sec, setSec] = useState({
    two_factor_enabled: false,
    login_alerts_enabled: true,
    session_timeout_minutes: 480,
  });
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorRecoveries, setTwoFactorRecoveries] = useState([]);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableForm, setDisableForm] = useState({ password: '', code: '' });
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [regeneratedCodes, setRegeneratedCodes] = useState([]);

  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [privacyExports, setPrivacyExports] = useState([]);
  const [privacyDashboard, setPrivacyDashboard] = useState({
    latest_exports: [],
    account_deletion_requested: false,
    scheduled_deletion_date: null,
    deletion_request: null,
  });
  const [privacyAction, setPrivacyAction] = useState(null);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [deletionForm, setDeletionForm] = useState({
    reason: 'No longer needed',
    password: '',
    confirmation: '',
  });
  const [privacyConfig, setPrivacyConfig] = useState(null);

  const visibleRecoveryCodes = twoFactorRecoveries.length ? twoFactorRecoveries : regeneratedCodes;

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const response = await getSettings();
        const settings = response?.data?.settings || {};
        if (!active) return;
        setNotif(prev => ({ ...DEFAULT_NOTIFICATIONS, ...settings.notifications, ...prev }));
        await loadSecuritySettings();
        await loadPreferences();
      } catch (err) {
        if (!active) return;
        toastError(err?.message || t("toast_load_settings_failed"));
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => { active = false; };
  }, [loadPreferences, loadSecuritySettings]);

  useEffect(() => {
    if (security) {
      setSec({
        two_factor_enabled: security.two_factor_enabled ?? false,
        login_alerts_enabled: security.login_alerts_enabled ?? true,
        session_timeout_minutes: security.session_timeout_minutes ?? 480,
      });
    }
  }, [security]);

  useEffect(() => {
    if (tab !== 4) return undefined;

    let active = true;

    const loadPrivacyData = async () => {
      setPrivacyLoading(true);
      try {
        const [dashboardResponse, exportsResponse, configResponse] = await Promise.all([
          getPrivacyDashboard(),
          getPrivacyExports(),
          getPrivacyConfig().catch(() => null)
        ]);

        if (!active) return;
        setPrivacyDashboard(dashboardResponse?.data || dashboardResponse || {});
        setPrivacyExports(exportsResponse?.data || exportsResponse || []);
        if (configResponse?.data) {
          setPrivacyConfig(configResponse.data);
        }
      } catch (err) {
        if (active) toastError(err?.message || t("toast_load_privacy_failed"));
      } finally {
        if (active) setPrivacyLoading(false);
      }
    };

    loadPrivacyData();
    const timer = window.setInterval(loadPrivacyData, 15000);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [tab]);

  const nt = key => setNotif(prev => ({ ...prev, [key]: !prev[key] }));
  const ap = key => e => setAppearance(prev => ({ ...prev, [key]: e.target.value }));

  const formatDate = (value) => (value ? new Date(value).toLocaleString() : '-');
  const formatBytes = (bytes) => {
    const value = Number(bytes || 0);
    if (!value) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
    return `${(value / (1024 ** index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
  };

  const copyRecoveryCodes = async () => {
    if (!visibleRecoveryCodes.length) return toastError(t("toast_no_recovery_copy"));
    try {
      await navigator.clipboard.writeText(visibleRecoveryCodes.join('\n'));
      toastSuccess(t("toast_recovery_copied"));
    } catch {
      toastError(t("toast_recovery_copy_failed"));
    }
  };

  const downloadRecoveryCodes = () => {
    if (!visibleRecoveryCodes.length) return toastError(t("toast_no_recovery_download"));
    const content = [
      'Recovery Codes',
      '',
      ...visibleRecoveryCodes.map(code => code.toString()),
      '',
      'Keep these codes safe. Each code can be used once.',
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toastSuccess(t("toast_recovery_downloaded"));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({ notifications: notif });
      await savePreferences({
        date_format: appearance.date_format,
        theme: appearance.theme,
        language: appearance.language,
      });
      const securityResponse = await saveSecuritySettings({
        two_factor_enabled: sec.two_factor_enabled,
        login_alerts_enabled: sec.login_alerts_enabled,
        session_timeout_minutes: Number(sec.session_timeout_minutes),
      });

      if (securityResponse?.meta?.requires_two_factor_setup) {
        setTwoFactorCode('');
        setShowTwoFactorModal(true);
        toastSuccess(t("toast_2fa_setup_started"));
      } else {
        toastSuccess(t("toast_settings_saved"));
      }

      applyTheme(appearance.theme);
    } catch (err) {
      toastError(err?.message || t("toast_save_settings_failed"));
    } finally {
      setSaving(false);
    }
  };

  const runExport = async (type) => {
    setPrivacyAction(type);
    try {
      const response =
        type === 'full'
          ? await requestFullPrivacyExport()
          : type === 'financial'
            ? await requestFinancialPrivacyExport()
            : await requestAuditLogPrivacyExport();

      toastSuccess(response?.message || t("toast_export_submitted"));
      const [dashboardResponse, exportsResponse] = await Promise.all([getPrivacyDashboard(), getPrivacyExports()]);
      setPrivacyDashboard(dashboardResponse?.data || dashboardResponse || {});
      setPrivacyExports(exportsResponse?.data || exportsResponse || []);
    } catch (err) {
      toastError(err?.message || t("toast_export_failed"));
    } finally {
      setPrivacyAction(null);
    }
  };

  const handleExportDownload = async (item) => {
    if (!item?.download_url) return toastError(t("toast_export_not_ready"));
    setPrivacyAction(`download-${item.id}`);
    try {
      const response = await downloadPrivacyExport(item.download_url);
      const blob = response instanceof Blob ? response : new Blob([response]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.file_name || `privacy-export-${item.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toastSuccess(t("toast_export_downloaded"));
      const exportsResponse = await getPrivacyExports();
      setPrivacyExports(exportsResponse?.data || exportsResponse || []);
    } catch (err) {
      toastError(err?.message || t("toast_export_download_failed"));
    } finally {
      setPrivacyAction(null);
    }
  };

  const submitDeletionRequest = async () => {
    if (deletionForm.confirmation !== 'DELETE') {
      return toastError(t("toast_type_delete"));
    }

    try {
      const response = await deleteAccountImmediately(deletionForm);
      toastSuccess(response?.message || "Account scheduled for immediate deletion and anonymization.");
      setShowDeletionModal(false);
      setDeletionForm({ reason: 'No longer needed', password: '', confirmation: '' });
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      toastError(err?.message || t("toast_deletion_failed"));
    }
  };

  const submitDeactivationRequest = async () => {
    if (deletionForm.confirmation !== 'DEACTIVATE') {
      return toastError("Please type DEACTIVATE to confirm.");
    }

    try {
      const response = await deactivateAccount(deletionForm);
      toastSuccess(response?.message || "Account deactivated.");
      setShowDeletionModal(false);
      setDeletionForm({ reason: 'No longer needed', password: '', confirmation: '' });
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      toastError(err?.message || "Deactivation failed.");
    }
  };

  const cancelDeletionRequest = async () => {
    try {
      const response = await cancelAccountDeletion();
      toastSuccess(response?.message || t("toast_deletion_cancelled"));
      const dashboardResponse = await getPrivacyDashboard();
      setPrivacyDashboard(dashboardResponse?.data || dashboardResponse || {});
    } catch (err) {
      toastError(err?.message || t("toast_deletion_cancel_failed"));
    }
  };

  return (
    <div className="fade-up space-y-4 max-w-3xl">
      <div className="mb-2">
        <Tabs tabs={[t("tab_notifications"), t("tab_language"), t("tab_appearance"), t("tab_security"), t("tab_data_privacy")]} active={tab} onChange={setTab} scrollable={true} />
      </div>

      {tab === 0 && (
        <div className="space-y-4">
          <Card>
            <SectionHeader title={t("title_email_notifications")} subtitle={loading ? t("desc_loading_preferences") : t("desc_email_notifications")} />
            <Toggle on={notif.email_maintenance} onToggle={() => nt('email_maintenance')} label={t("toggle_email_maintenance")} desc={t("toggle_desc_email_maintenance")} />
            <Toggle on={notif.email_payments} onToggle={() => nt('email_payments')} label={t("toggle_email_payments")} desc={t("toggle_desc_email_payments")} />
            <Toggle on={notif.email_vendors} onToggle={() => nt('email_vendors')} label={t("toggle_email_vendors")} desc={t("toggle_desc_email_vendors")} />
            <Toggle on={notif.email_alerts} onToggle={() => nt('email_alerts')} label={t("toggle_email_alerts")} desc={t("toggle_desc_email_alerts")} />
          </Card>
          <Card>
            <SectionHeader title={t("title_sms_notifications")} subtitle={t("desc_sms_notifications")} />
            <Toggle on={notif.sms_alerts} onToggle={() => nt('sms_alerts')} label={t("toggle_sms_alerts")} desc={t("toggle_desc_sms_alerts")} />
            <Toggle on={notif.sms_payments} onToggle={() => nt('sms_payments')} label={t("toggle_sms_payments")} desc={t("toggle_desc_sms_payments")} />
            <Toggle on={notif.sms_maintenance} onToggle={() => nt('sms_maintenance')} label={t("toggle_sms_maintenance")} desc={t("toggle_desc_sms_maintenance")} />
          </Card>
          <Card>
            <SectionHeader title={t("title_push_notifications")} />
            <Toggle on={notif.push_all} onToggle={() => nt('push_all')} label={t("toggle_push_all")} desc={t("toggle_desc_push_all")} />
            <Toggle on={notif.push_fire} onToggle={() => nt('push_fire')} label={t("toggle_push_fire")} desc={t("toggle_desc_push_fire")} />
            <Toggle on={notif.push_tasks} onToggle={() => nt('push_tasks')} label={t("toggle_push_tasks")} desc={t("toggle_desc_push_tasks")} />
          </Card>
          <Card>
            <SectionHeader title={t("title_digest_emails")} />
            <Toggle on={notif.digest_daily} onToggle={() => nt('digest_daily')} label={t("toggle_digest_daily")} desc={t("toggle_desc_digest_daily")} />
            <Toggle on={notif.digest_weekly} onToggle={() => nt('digest_weekly')} label={t("toggle_digest_weekly")} desc={t("toggle_desc_digest_weekly")} />
          </Card>
          <Card>
            <SectionHeader title="Promotional Notifications" subtitle="Receive updates about new features, partner offers, and promotional content" />
            <Toggle on={notif.email_promo} onToggle={() => nt('email_promo')} label="Promotional Emails" desc="Receive promotional content via email" />
            <Toggle on={notif.sms_promo} onToggle={() => nt('sms_promo')} label="Promotional SMS" desc="Receive promotional content via SMS" />
            <Toggle on={notif.push_promo} onToggle={() => nt('push_promo')} label="Promotional Push" desc="Receive promotional push notifications" />
          </Card>
        </div>
      )}

      {tab === 1 && (
        <Card>
          <SectionHeader title={t("title_language_preferences")} subtitle={t("desc_language_preferences")} />
          <div className="grid gap-4 sm:grid-cols-2 mt-4">
            {[
              { id: 'en', label: t("lang_en"), desc: t("lang_en_desc") },
              { id: 'hi', label: t("lang_hi"), desc: t("lang_regional_desc") },
              { id: 'gu', label: t("lang_gu"), desc: t("lang_regional_desc") },
            ].map(l => (
              <div
                key={l.id}
                onClick={() => setAppearance(prev => ({ ...prev, language: l.id }))}
                className={`flex flex-col gap-1 p-4 rounded-xl border-2 cursor-pointer transition-all bg-white dark:bg-slate-800 ${appearance.language === l.id ? 'border-primary' : 'border-slate-200 dark:border-slate-700'}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-semibold ${appearance.language === l.id ? 'text-primary' : 'text-ink dark:text-slate-100'}`}>{l.label}</span>
                  {appearance.language === l.id && <Icon name="check-circle" size={20} color="#4f46e5" />}
                </div>
                <span className="text-xs text-muted dark:text-slate-400">{l.desc}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === 2 && (
        <Card>
          <SectionHeader title={t("title_display_preferences")} subtitle={t("desc_display_preferences")} />
          <FormRow cols={2}>
            <Field label={t("label_date_format")} type="select" value={appearance.date_format} onChange={ap('date_format')} options={['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']} />
          </FormRow>
          <div className="mt-2">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{t("label_theme")}</label>
            <div className="flex gap-3">
              {[
                { id: 'light', label: t("theme_light"), icon: "sun" },
                { id: 'dark', label: t("theme_dark"), icon: "moon" },
                { id: 'system', label: t("theme_system"), icon: "globe" },
              ].map(t => (
                <div
                  key={t.id}
                  onClick={() => setAppearance(prev => ({ ...prev, theme: t.id }))}
                  className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all bg-white dark:bg-slate-800 ${appearance.theme === t.id ? 'border-primary' : 'border-slate-200 dark:border-slate-700'}`}
                >
                  <Icon name={t.icon} size={20} color={appearance.theme === t.id ? '#4f46e5' : '#94a3b8'} />
                  <span className={`text-xs font-semibold ${appearance.theme === t.id ? 'text-primary' : 'text-muted'}`}>{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {tab === 3 && (
        <Card>
          <SectionHeader title={t("title_account_security")} subtitle={securityLoading ? t("desc_loading_security") : t("desc_account_security")} />
          <Toggle
            on={sec.two_factor_enabled}
            onToggle={() => {
              if (sec.two_factor_enabled) {
                setShowDisableModal(true);
                return;
              }
              setSec(prev => ({ ...prev, two_factor_enabled: true }));
            }}
            label={t("label_2fa")}
            desc={t("desc_2fa")}
          />
          <Toggle on={sec.login_alerts_enabled} onToggle={() => setSec(prev => ({ ...prev, login_alerts_enabled: !prev.login_alerts_enabled }))} label={t("label_login_alerts")} desc={t("desc_login_alerts")} />
          <div className="pt-3">
            <Field label={t("label_session_timeout")} type="select" value={sec.session_timeout_minutes} onChange={e => setSec(prev => ({ ...prev, session_timeout_minutes: Number(e.target.value) }))} options={timeoutOptions} />
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <Btn
              sm
              variant="outline_primary"
              icon="refresh"
              onClick={async () => {
                try {
                  const res = await regenerateRecoveryCodes({ password: recoveryPassword || '' });
                  const codes = res?.data?.recovery_codes || res?.recovery_codes || [];
                  setRegeneratedCodes(codes);
                  setShowRecoveryModal(true);
                  toastSuccess(t("toast_recovery_regenerated"));
                } catch (err) {
                  toastError(err?.message || t("toast_recovery_regenerate_failed"));
                }
              }}
            >
              Regenerate Recovery Codes
            </Btn>
            {sec.two_factor_enabled && (
              <Btn sm variant="danger" icon="lock" onClick={() => setShowDisableModal(true)}>
                Disable 2FA
              </Btn>
            )}
          </div>
        </Card>
      )}

      {tab === 4 && (
        <div className="space-y-4">
          {privacyDashboard.account_deletion_requested && (
            <Alert type="warn">{t("msg_deletion_scheduled", { date: formatDate(privacyDashboard.scheduled_deletion_date) })}</Alert>
          )}

          <Card>
            <div className="flex items-start justify-between gap-3">
              <SectionHeader title={t("title_data_export")} subtitle={t("desc_data_export")} />
              <Btn sm variant="outline_primary" icon="refresh" loading={privacyLoading} onClick={async () => {
                setPrivacyLoading(true);
                try {
                  const [dashboardResponse, exportsResponse] = await Promise.all([getPrivacyDashboard(), getPrivacyExports()]);
                  setPrivacyDashboard(dashboardResponse?.data || dashboardResponse || {});
                  setPrivacyExports(exportsResponse?.data || exportsResponse || []);
                } catch (err) {
                  toastError(err?.message || t("toast_load_privacy_failed"));
                } finally {
                  setPrivacyLoading(false);
                }
              }}>
                Refresh Exports
              </Btn>
            </div>

            <div className="space-y-3">
              {[
                { type: 'full', label: t("export_full"), desc: t("export_desc_full"), icon: 'download' },
                { type: 'financial', label: t("export_financial"), desc: t("export_desc_financial"), icon: 'money' },
                { type: 'audit', label: t("export_audit"), desc: t("export_desc_audit"), icon: 'audit' },
              ].map((item) => (
                <div key={item.type} className="flex flex-col gap-3 py-3 border-b border-slate-100 dark:border-slate-700 last:border-0 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <Icon name={item.icon} size={15} className="text-muted" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-ink dark:text-slate-100">{item.label}</div>
                      <div className="text-xs text-muted dark:text-slate-400">{item.desc}</div>
                    </div>
                  </div>
                  <Btn xs icon="download" loading={privacyAction === item.type} onClick={() => runExport(item.type)}>
                    Export
                  </Btn>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <SectionHeader title={t("title_export_history")} subtitle={t("desc_export_history")} />
            <div className="space-y-2">
              {privacyExports.length > 0 ? privacyExports.map((item) => (
                <div key={item.id} className="flex flex-col gap-3 rounded-xl border border-border dark:border-slate-700 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink dark:text-slate-100 capitalize">
                      {String(item.type || '').replace(/_/g, ' ')}
                    </div>
                    <div className="text-xs text-muted dark:text-slate-400">
                      {t("export_status_requested", { status: item.status_label || item.status, date: formatDate(item.requested_at || item.created_at) })}
                      {item.completed_at ? t("export_status_completed", { date: formatDate(item.completed_at) }) : ""}
                      {item.file_size ? t("export_status_size", { size: formatBytes(item.file_size) }) : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${item.status === 'completed' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                      : item.status === 'processing' ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300'
                        : item.status === 'failed' ? 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300'
                          : item.status === 'expired' ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                            : 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
                      }`}>
                      {item.status_label || item.status}
                    </span>
                    <Btn
                      xs
                      variant="outline_primary"
                      icon="download"
                      loading={privacyAction === `download-${item.id}`}
                      disabled={!item.download_url}
                      onClick={() => handleExportDownload(item)}
                    >
                      Download
                    </Btn>
                  </div>
                </div>
              )) : (
                <div className="text-sm text-muted dark:text-slate-400 py-6 text-center">{t("empty_exports")}</div>
              )}
            </div>
          </Card>

          <Card>
            <SectionHeader title={t("title_delete_account")} subtitle={t("desc_delete_account")} />
            <Alert type="warn">{t("warn_delete_account")}</Alert>
            <div className="flex flex-wrap gap-3 mt-4">
              <Btn sm variant="danger" icon="trash" onClick={() => setShowDeletionModal(true)}>
                Delete or Deactivate My Account
              </Btn>
              {privacyDashboard.account_deletion_requested && (
                <Btn sm variant="outline_primary" icon="refresh" onClick={cancelDeletionRequest}>
                  Cancel Deletion
                </Btn>
              )}
            </div>
          </Card>
        </div>
      )}

      {tab === 4 && privacyConfig?.grievance_officer_name && (
        <Card className="mt-4 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
          <SectionHeader title="Grievance Officer" subtitle="Contact details for privacy and data concerns" />
          <div className="grid gap-2 mt-4 text-sm text-ink dark:text-slate-200">
            <div><strong>Name:</strong> {privacyConfig.grievance_officer_name}</div>
            {privacyConfig.grievance_officer_email && <div><strong>Email:</strong> <a href={`mailto:${privacyConfig.grievance_officer_email}`} className="text-primary">{privacyConfig.grievance_officer_email}</a></div>}
            {privacyConfig.grievance_officer_phone && <div><strong>Phone:</strong> {privacyConfig.grievance_officer_phone}</div>}
          </div>
          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700 text-sm">
            For more details, please read our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline">Privacy Policy</a>.
          </div>
        </Card>
      )}

      {/* Sticky Save Button */}
      <div className="sticky bottom-0 z-20 mt-8 pt-4 pb-2 bg-slate-50/80 dark:bg-slate-950 backdrop-blur-md sm:bg-transparent sm:backdrop-blur-none sm:pt-0 sm:pb-3">
        <Btn className="w-full justify-center shadow-lg shadow-indigo-500/20" variant="primary" icon="check" loading={saving} onClick={handleSave}>
          {saving ? t("btn_saving_settings") : t("btn_save_settings")}
        </Btn>
      </div>

      {showTwoFactorModal && securitySetup && (
        <Modal title={t("title_setup_2fa")} onClose={() => { setShowTwoFactorModal(false); setTwoFactorCode(''); }} wide>
          <div className="space-y-4">
            <Alert type="info">{t("info_scan_qr")}</Alert>
            <div className="flex justify-center">
              <img src={securitySetup.qr_code} alt="2FA QR code" className="max-w-[240px] rounded-xl border border-border bg-white p-3" />
            </div>
            <div className="grid gap-3">
              <Field label={t("label_verification_code")} value={twoFactorCode} onChange={e => setTwoFactorCode(e.target.value)} placeholder={t("placeholder_verification_code")} />
              <div className="flex justify-end gap-3">
                <Btn variant="default" onClick={() => setShowTwoFactorModal(false)}>{t("btn_cancel")}</Btn>
                <Btn
                  variant="primary"
                  loading={securitySaving}
                  onClick={async () => {
                    try {
                      const response = await verifyTwoFactorSetup({
                        challenge_token: securitySetup.challenge_token,
                        code: twoFactorCode,
                      });
                      const codes = response?.data?.recovery_codes || response?.recovery_codes || [];
                      setTwoFactorRecoveries(codes);
                      setShowTwoFactorModal(false);
                      setShowRecoveryModal(true);
                      toastSuccess(t("toast_2fa_enabled"));
                    } catch (err) {
                      toastError(err?.message || t("toast_verify_code_failed"));
                    }
                  }}
                >
                  Verify Code
                </Btn>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showDisableModal && (
        <Modal title={t("title_disable_2fa")} onClose={() => { setShowDisableModal(false); setDisableForm({ password: '', code: '' }); }} wide>
          <div className="space-y-4">
            <Alert type="warn">{t("warn_disable_2fa")}</Alert>
            <FormRow cols={1}>
              <Field label={t("label_password")} type="password" value={disableForm.password} onChange={e => setDisableForm(prev => ({ ...prev, password: e.target.value }))} />
              <Field label={t("label_verification_or_recovery")} value={disableForm.code} onChange={e => setDisableForm(prev => ({ ...prev, code: e.target.value }))} />
            </FormRow>
            <div className="flex justify-end gap-3">
              <Btn variant="default" onClick={() => setShowDisableModal(false)}>{t("btn_cancel")}</Btn>
              <Btn
                variant="danger"
                loading={securitySaving}
                onClick={async () => {
                  try {
                    await disableTwoFactor({
                      password: disableForm.password || undefined,
                      code: disableForm.code || undefined,
                    });
                    setShowDisableModal(false);
                    setDisableForm({ password: '', code: '' });
                    toastSuccess(t("toast_2fa_disabled"));
                  } catch (err) {
                    toastError(err?.message || t("toast_disable_2fa_failed"));
                  }
                }}
              >
                Disable 2FA
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {showRecoveryModal && (
        <Modal title={t("title_recovery_codes")} onClose={() => { setShowRecoveryModal(false); setRecoveryPassword(''); }} wide>
          <div className="space-y-4">
            <Alert type="warn">{t("warn_recovery_codes")}</Alert>
            <Field label={t("label_password_confirmation")} type="password" value={recoveryPassword} onChange={e => setRecoveryPassword(e.target.value)} />
            <div className="grid gap-2">
              {(twoFactorRecoveries.length ? twoFactorRecoveries : regeneratedCodes).map(code => (
                <div key={code} className="rounded-lg border border-border bg-slate-50 dark:bg-slate-800 px-3 py-2 font-mono text-sm text-ink">
                  {code}
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
              <div className="flex flex-wrap gap-2">
                <Btn variant="outline_primary" onClick={copyRecoveryCodes}>{t("btn_copy_codes")}</Btn>
                <Btn variant="outline_primary" onClick={downloadRecoveryCodes}>{t("btn_download_txt")}</Btn>
              </div>
              <div className="flex flex-wrap justify-end gap-3">
                <Btn variant="default" onClick={() => { setShowRecoveryModal(false); setRecoveryPassword(''); }}>
                  I have saved them
                </Btn>
                <Btn
                  variant="primary"
                  onClick={async () => {
                    try {
                      if (!recoveryPassword) {
                        toastError(t("toast_enter_password_recovery"));
                        return;
                      }
                      const response = await regenerateRecoveryCodes({ password: recoveryPassword });
                      const codes = response?.data?.recovery_codes || response?.recovery_codes || [];
                      setRegeneratedCodes(codes);
                      setTwoFactorRecoveries(codes);
                      toastSuccess(t("toast_recovery_regenerated"));
                    } catch (err) {
                      toastError(err?.message || t("toast_recovery_regenerate_failed"));
                    }
                  }}
                >
                  Regenerate Again
                </Btn>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showDeletionModal && (
        <Modal title={t("title_delete_account")} onClose={() => setShowDeletionModal(false)} wide>
          <div className="space-y-4">
            <Alert type="warn">{t("warn_deletion_modal")}</Alert>
            <FormRow cols={1}>
              <Field
                label={t("label_reason")}
                type="select"
                value={deletionForm.reason}
                onChange={e => setDeletionForm(prev => ({ ...prev, reason: e.target.value }))}
                options={DELETE_REASON_OPTIONS}
              />
              <Field
                label={t("label_current_password")}
                type="password"
                value={deletionForm.password}
                onChange={e => setDeletionForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder={t("placeholder_current_password")}
              />
              <Field
                label={t("label_type_delete")}
                value={deletionForm.confirmation}
                onChange={e => setDeletionForm(prev => ({ ...prev, confirmation: e.target.value }))}
                placeholder={t("placeholder_delete")}
              />
            </FormRow>
            <div className="flex flex-col gap-2 mt-4 sm:flex-row sm:justify-end sm:gap-3">
              <Btn variant="default" onClick={() => setShowDeletionModal(false)}>{t("btn_cancel")}</Btn>
              <Btn variant="outline_primary" onClick={submitDeactivationRequest} disabled={deletionForm.confirmation !== 'DEACTIVATE'}>
                Deactivate (Type DEACTIVATE)
              </Btn>
              <Btn variant="danger" onClick={submitDeletionRequest} disabled={deletionForm.confirmation !== 'DELETE'}>
                Delete Immediately
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
